import React, { useState, useEffect, useRef, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from "recharts";
import { ArrowLeft, Users, Eye, Clock, MousePointerClick, Target, BarChart3 } from "lucide-react";
import api from "../api/client";

const RANGES = ["7d", "30d", "90d"];

function StatCard({ icon: Icon, label, value, color = "text-copper" }) {
  return (
    <div className="bg-dark-300 border border-dark-50 rounded-xl p-4">
      <div className="flex items-center gap-2 mb-2">
        <Icon size={14} className={color} />
        <span className="text-xs text-gray-500">{label}</span>
      </div>
      <div className="text-xl font-mono font-bold">{value ?? "—"}</div>
    </div>
  );
}

function ScrollFunnel({ data }) {
  if (!data) return null;
  const steps = [
    { label: "25%", value: data.pct_25 },
    { label: "50%", value: data.pct_50 },
    { label: "75%", value: data.pct_75 },
    { label: "100%", value: data.pct_100 },
  ];
  const max = Math.max(...steps.map((s) => s.value || 0), 1);

  return (
    <div className="bg-dark-300 border border-dark-50 rounded-xl p-5">
      <h3 className="text-sm font-semibold text-gray-300 mb-4">
        Scroll Depth Funnel
      </h3>
      <div className="space-y-3">
        {steps.map((step) => (
          <div key={step.label} className="flex items-center gap-3">
            <span className="text-xs text-gray-500 w-10 text-right font-mono">
              {step.label}
            </span>
            <div className="flex-1 bg-dark-50/50 rounded-full h-6 overflow-hidden">
              <div
                className="h-full bg-copper/60 rounded-full flex items-center justify-end pr-2"
                style={{ width: `${((step.value || 0) / max) * 100}%` }}
              >
                <span className="text-xs font-mono text-white">
                  {step.value?.toLocaleString() || 0}
                </span>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function HeatmapCanvas({ heatData, width = 600, height = 800 }) {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || !heatData?.length) return;
    const ctx = canvas.getContext("2d");
    ctx.clearRect(0, 0, width, height);

    // Draw a simple page background
    ctx.fillStyle = "#1a1a2e";
    ctx.fillRect(0, 0, width, height);

    const maxCount = Math.max(...heatData.map((d) => d.count), 1);

    heatData.forEach((point) => {
      const x = (point.x / 100) * width;
      const y = (point.y / 100) * height;
      const intensity = point.count / maxCount;
      const radius = 15 + intensity * 25;

      const gradient = ctx.createRadialGradient(x, y, 0, x, y, radius);
      gradient.addColorStop(0, `rgba(255, 0, 0, ${0.3 + intensity * 0.5})`);
      gradient.addColorStop(0.5, `rgba(255, 165, 0, ${0.1 + intensity * 0.2})`);
      gradient.addColorStop(1, "rgba(0, 0, 0, 0)");

      ctx.beginPath();
      ctx.fillStyle = gradient;
      ctx.arc(x, y, radius, 0, Math.PI * 2);
      ctx.fill();
    });
  }, [heatData, width, height]);

  if (!heatData?.length) {
    return (
      <div className="flex items-center justify-center h-64 text-gray-500 text-sm">
        No heatmap data available
      </div>
    );
  }

  return (
    <canvas
      ref={canvasRef}
      width={width}
      height={height}
      className="rounded-lg w-full"
      style={{ maxHeight: 500, objectFit: "contain" }}
    />
  );
}

export default function BehaviorPageDetail({ selectedSite }) {
  const { path } = useParams();
  const navigate = useNavigate();
  const decodedPath = decodeURIComponent(path);
  const [data, setData] = useState(null);
  const [heatData, setHeatData] = useState([]);
  const [heatType, setHeatType] = useState("click");
  const [range, setRange] = useState("7d");
  const [loading, setLoading] = useState(true);

  const fetchHeatmap = useCallback(() => {
    if (!selectedSite) return;
    api
      .get("/behavior/heatmap/", {
        params: {
          website_id: selectedSite.id,
          path: decodedPath,
          type: heatType,
          range,
        },
      })
      .then((res) => setHeatData(res.data.points || res.data))
      .catch(() => setHeatData([]));
  }, [selectedSite, decodedPath, heatType, range]);

  useEffect(() => {
    if (!selectedSite) return;
    setLoading(true);
    api
      .get("/behavior/page-detail/", {
        params: { website_id: selectedSite.id, path: decodedPath, range },
      })
      .then((res) => setData(res.data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [selectedSite, decodedPath, range]);

  useEffect(() => {
    fetchHeatmap();
  }, [fetchHeatmap]);

  if (!selectedSite) {
    return (
      <div className="text-gray-500 text-center py-20">
        Select a website to view page details
      </div>
    );
  }

  const stats = data?.stats || {};
  const daily = (data?.daily || []).map((d) => ({
    date: new Date(d.date).toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
    }),
    visitors: d.visitors,
    pageviews: d.pageviews,
  }));

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <button
            onClick={() => navigate("/behavior/pages")}
            className="p-2 hover:bg-dark-50/50 rounded-lg transition-colors"
          >
            <ArrowLeft size={18} className="text-gray-400" />
          </button>
          <div>
            <h2 className="text-lg font-semibold">Page Detail</h2>
            <p className="text-xs text-gray-500 font-mono">{decodedPath}</p>
          </div>
        </div>
        <div className="flex bg-dark-300 border border-dark-50 rounded-lg p-0.5">
          {RANGES.map((r) => (
            <button
              key={r}
              onClick={() => setRange(r)}
              className={`px-3 py-1.5 text-xs rounded-md transition-colors ${
                range === r
                  ? "bg-copper/20 text-copper"
                  : "text-gray-400 hover:text-white"
              }`}
            >
              {r}
            </button>
          ))}
        </div>
      </div>

      {loading ? (
        <div className="animate-pulse space-y-4">
          <div className="grid grid-cols-4 gap-4">
            {[...Array(4)].map((_, i) => (
              <div key={i} className="h-24 bg-dark-300 rounded-xl" />
            ))}
          </div>
          <div className="h-64 bg-dark-300 rounded-xl" />
        </div>
      ) : (
        <>
          {/* Stat cards */}
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3">
            <StatCard icon={Users} label="Visitors" value={stats.visitors?.toLocaleString()} />
            <StatCard icon={Eye} label="Pageviews" value={stats.pageviews?.toLocaleString()} color="text-blue-400" />
            <StatCard icon={Clock} label="Avg Time" value={stats.avg_time ? `${stats.avg_time.toFixed(1)}s` : undefined} color="text-green-400" />
            <StatCard icon={BarChart3} label="Avg Scroll" value={stats.avg_scroll ? `${stats.avg_scroll.toFixed(0)}%` : undefined} color="text-purple-400" />
            <StatCard icon={MousePointerClick} label="Clicks" value={stats.clicks?.toLocaleString()} color="text-amber-400" />
            <StatCard icon={Target} label="Conversions" value={stats.conversions?.toLocaleString()} color="text-emerald-400" />
            <StatCard icon={BarChart3} label="Engagement" value={stats.engagement?.toFixed(0)} />
          </div>

          {/* Scroll funnel */}
          <ScrollFunnel data={data?.scroll_funnel} />

          {/* Daily chart */}
          {daily.length > 1 && (
            <div className="bg-dark-300 border border-dark-50 rounded-xl p-5">
              <h3 className="text-sm font-semibold text-gray-300 mb-4">
                Daily Metrics
              </h3>
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={daily}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#1e293b" />
                    <XAxis
                      dataKey="date"
                      tick={{ fill: "#64748b", fontSize: 11 }}
                      axisLine={{ stroke: "#1e293b" }}
                    />
                    <YAxis
                      tick={{ fill: "#64748b", fontSize: 11 }}
                      axisLine={{ stroke: "#1e293b" }}
                    />
                    <Tooltip
                      contentStyle={{
                        backgroundColor: "#111827",
                        border: "1px solid #1e293b",
                        borderRadius: "8px",
                        fontSize: "12px",
                      }}
                    />
                    <Line
                      type="monotone"
                      dataKey="visitors"
                      stroke="#b87333"
                      strokeWidth={2}
                      dot={false}
                    />
                    <Line
                      type="monotone"
                      dataKey="pageviews"
                      stroke="#60a5fa"
                      strokeWidth={2}
                      dot={false}
                    />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </div>
          )}

          {/* Heatmap */}
          <div className="bg-dark-300 border border-dark-50 rounded-xl p-5">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-sm font-semibold text-gray-300">
                Heatmap
              </h3>
              <div className="flex bg-dark-50/50 rounded-lg p-0.5">
                <button
                  onClick={() => setHeatType("click")}
                  className={`px-3 py-1.5 text-xs rounded-md transition-colors ${
                    heatType === "click"
                      ? "bg-copper/20 text-copper"
                      : "text-gray-400"
                  }`}
                >
                  Clicks
                </button>
                <button
                  onClick={() => setHeatType("scroll")}
                  className={`px-3 py-1.5 text-xs rounded-md transition-colors ${
                    heatType === "scroll"
                      ? "bg-copper/20 text-copper"
                      : "text-gray-400"
                  }`}
                >
                  Scroll
                </button>
              </div>
            </div>
            <HeatmapCanvas heatData={heatData} />
          </div>
        </>
      )}
    </div>
  );
}
